import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { Logo } from '@/components/common/Logo';
import { Input, Select, Checkbox } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { AU_STATES, RESIDENCY_STATUSES, SENIORITY_LEVELS, SPECIALTIES } from '@/lib/australia';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';
import { AhpraVerifyPanel } from '@/components/common/AhpraVerifyPanel';
import { getSupabase, isSupabaseConfigured } from '@/services/supabase';
import type { AhpraRegistration } from '@/types/ahpra';

interface FormState {
  firstName: string;
  surname: string;
  email: string;
  phone: string;
  password: string;
  seniority: string;
  specialty: string;
  primaryState: string;
  residency: string;
  ahpraVerified: AhpraRegistration | null;
  termsAccepted: boolean;
}

const initialState: FormState = {
  firstName: '',
  surname: '',
  email: '',
  phone: '',
  password: '',
  seniority: '',
  specialty: '',
  primaryState: '',
  residency: '',
  ahpraVerified: null,
  termsAccepted: false,
};

export function RegisterPage() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(initialState);
  const { signIn, isMockAuth } = useAuth();
  const toast = useToast();
  const nav = useNavigate();

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onAhpraVerified = (reg: AhpraRegistration) => {
    update('ahpraVerified', reg);
    if (reg.specialties.length > 0 && !form.specialty) {
      update('specialty', reg.specialties[0]!);
    }
  };

  const validateStep1 = (): string | null => {
    if (!form.firstName.trim() || !form.surname.trim()) return 'First name and surname are required.';
    if (!form.email.trim()) return 'Email is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Please enter a valid email address.';
    if (!form.phone.trim()) return 'Mobile number is required.';
    if (form.password.length < 8) return 'Password must be at least 8 characters.';
    return null;
  };

  const next1 = () => {
    const err = validateStep1();
    if (err) { setError(err); return; }
    setError(null);
    setStep(2);
  };

  const next2 = () => {
    if (!form.specialty || !form.primaryState || !form.residency) {
      setError('Please fill all fields on this step.');
      return;
    }
    setError(null);
    setStep(3);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.termsAccepted) { setError('You must confirm AHPRA registration eligibility.'); return; }
    setError(null);
    setSubmitting(true);

    // ── Mock path (Phase 1) ───────────────────────────────────────────
    if (isMockAuth || !isSupabaseConfigured) {
      await new Promise((r) => setTimeout(r, 600));
      await signIn(form.email, form.password);
      toast.success('Welcome to Locum Network', 'Continue your onboarding in the portal.');
      nav('/portal/dashboard');
      return;
    }

    // ── Real path (Phase 2) ──────────────────────────────────────────
    // Signup is a single supabase.auth.signUp() call — the database
    // trigger `handle_new_auth_user` (migration 0022) reads the metadata
    // we pass here and creates the profile + doctor rows server-side.
    // This works even when "Confirm email" is on (no session yet at the
    // moment of signup) because the trigger runs as superuser, bypassing
    // RLS.
    const supa = getSupabase();
    const { data: signUpData, error: signUpErr } = await supa.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        // All metadata fields the trigger reads from
        data: {
          first_name: form.firstName,
          surname: form.surname,
          phone: form.phone,
          primary_specialty: form.specialty,
          primary_state: form.primaryState,
          residency: form.residency,
          seniority: form.seniority,
          ahpra_registration: form.ahpraVerified?.registrationNumber ?? '',
        },
        emailRedirectTo: `${window.location.origin}/login`,
      },
    });
    if (signUpErr) {
      setSubmitting(false);
      setError(signUpErr.message);
      return;
    }
    if (!signUpData.user) {
      setSubmitting(false);
      setError('Sign-up returned no user. Please try again.');
      return;
    }

    // If email confirmation is enabled, signUp returns a user but no
    // session. Land them on the login page with a confirmation prompt.
    if (!signUpData.session) {
      setSubmitting(false);
      toast.success(
        'Check your inbox',
        `We've sent a confirmation link to ${form.email}.`,
      );
      nav('/login?confirm=pending', { replace: true });
      return;
    }

    // Email confirmation off — straight to the portal.
    toast.success(
      'Welcome to Locum Network',
      'Continue your onboarding in the Compliance Locker.',
    );

    // We have a session — go straight to the portal dashboard.
    setSubmitting(false);
    nav('/portal/dashboard', { replace: true });
  };

  return (
    <div className="container-narrow py-12 lg:py-16">
      <Logo />
      <div className="mt-10 grid gap-8 lg:grid-cols-3">
        <aside className="lg:col-span-1 surface p-6 h-fit">
          <Badge tone="primary">Doctor registration</Badge>
          <h2 className="mt-3 font-display text-xl font-semibold text-ink">3 quick steps</h2>
          <ol className="mt-4 space-y-2.5 text-sm">
            {[
              ['About you', 1],
              ['Specialty & availability', 2],
              ['Compliance kick-off', 3],
            ].map(([label, n]) => (
              <li
                key={label as string}
                className={`flex items-center gap-2 ${step >= (n as number) ? 'text-ink' : 'text-ink-subtle'}`}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold ${
                    step >= (n as number) ? 'bg-primary text-primary-ink' : 'bg-muted text-ink-muted'
                  }`}
                >
                  {step > (n as number) ? <CheckCircle2 className="h-3 w-3" /> : (n as number)}
                </span>
                {label}
              </li>
            ))}
          </ol>
          <p className="mt-6 text-xs text-ink-muted">
            By registering you agree to our <Link to="/terms" className="link">terms</Link> and{' '}
            <Link to="/privacy" className="link">privacy policy</Link>.
          </p>
        </aside>

        <form onSubmit={submit} className="lg:col-span-2 surface p-6 space-y-5">
          {step === 1 && (
            <>
              <h1 className="font-display text-2xl font-bold tracking-tight text-ink">Create your account</h1>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input label="First name" required value={form.firstName} onChange={(e) => update('firstName', e.target.value)} />
                <Input label="Surname" required value={form.surname} onChange={(e) => update('surname', e.target.value)} />
                <Input label="Email" type="email" required value={form.email} onChange={(e) => update('email', e.target.value)} autoComplete="email" />
                <Input label="Mobile" type="tel" placeholder="+61 4__ ___ ___" required value={form.phone} onChange={(e) => update('phone', e.target.value)} autoComplete="tel" />
                <Input className="sm:col-span-2" label="Password" type="password" required value={form.password} onChange={(e) => update('password', e.target.value)} autoComplete="new-password" hint="8+ chars, mix of letters and numbers" />
              </div>
              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
                  <AlertCircle className="h-3.5 w-3.5" /> {error}
                </div>
              )}
              <div className="flex justify-end">
                <Button type="button" onClick={next1} iconRight={<ArrowRight className="h-4 w-4" />}>
                  Continue
                </Button>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <h1 className="font-display text-2xl font-bold tracking-tight text-ink">About your practice</h1>

              <AhpraVerifyPanel onVerified={onAhpraVerified} />

              <div className="grid gap-4 sm:grid-cols-2">
                <Select
                  label="Seniority"
                  value={form.seniority}
                  onChange={(e) => update('seniority', e.target.value)}
                  options={[{ value: '', label: 'Select…' }, ...SENIORITY_LEVELS.map((s) => ({ value: s.code, label: s.label }))]}
                />
                <Select
                  label="Primary specialty"
                  value={form.specialty}
                  onChange={(e) => update('specialty', e.target.value)}
                  options={[{ value: '', label: 'Select…' }, ...SPECIALTIES.map((s) => ({ value: s, label: s }))]}
                  hint={form.ahpraVerified?.specialties.length ? 'Pre-filled from your AHPRA registration' : undefined}
                />
                <Select
                  label="Primary state"
                  value={form.primaryState}
                  onChange={(e) => update('primaryState', e.target.value)}
                  options={[{ value: '', label: 'Select…' }, ...AU_STATES.map((s) => ({ value: s.code, label: s.name }))]}
                />
                <Select
                  className="sm:col-span-2"
                  label="Residency / work rights"
                  value={form.residency}
                  onChange={(e) => update('residency', e.target.value)}
                  options={[{ value: '', label: 'Select…' }, ...RESIDENCY_STATUSES.map((s) => ({ value: s.code, label: s.label }))]}
                />
              </div>
              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
                  <AlertCircle className="h-3.5 w-3.5" /> {error}
                </div>
              )}
              <div className="flex justify-between">
                <Button variant="ghost" type="button" onClick={() => { setError(null); setStep(1); }}>Back</Button>
                <Button type="button" onClick={next2} iconRight={<ArrowRight className="h-4 w-4" />}>
                  Continue
                </Button>
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <h1 className="font-display text-2xl font-bold tracking-tight text-ink">Compliance kick-off</h1>
              <p className="text-sm text-ink-muted">
                Once you finish creating your account, you'll land in the Compliance Locker — the
                fastest way to upload AHPRA, indemnity, police check, visa and more.
              </p>
              <ul className="rounded-xl border border-border bg-bg/40 p-4 space-y-2 text-sm">
                {['Bulk drag-and-drop', 'AI classifies file types and dates', 'Real-human review', 'Expiry alerts before they cost you'].map((p) => (
                  <li key={p} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" />{p}</li>
                ))}
              </ul>
              <Checkbox
                label="I confirm I am an Australian-registered medical practitioner (or eligible to be)."
                required
                checked={form.termsAccepted}
                onChange={(e) => update('termsAccepted', (e.target as HTMLInputElement).checked)}
              />
              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
                  <AlertCircle className="h-3.5 w-3.5" /> {error}
                </div>
              )}
              <div className="flex justify-between">
                <Button variant="ghost" type="button" onClick={() => { setError(null); setStep(2); }}>Back</Button>
                <Button type="submit" loading={submitting} iconRight={<ArrowRight className="h-4 w-4" />}>
                  Create account
                </Button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
